//
// Shared chrome for the small popups that hang off a side-menu button (the
// tutorials list, the update/install notice): a portal-rendered box anchored to
// the button's right edge, closed by clicking outside or pressing Escape.
// keepOpenSelector marks the triggering button so its own click does not count
// as an outside click (which would otherwise close then reopen the popup).
// Only `children` scroll, so title and footer stay reachable.
//

import PropTypes from 'prop-types';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const viewportMargin = 12;

// These buttons sit low in the side menu, so the box may outgrow the room below
// the anchor by this much and slide up to claim it.
const heightGrowth = 1.4;

function SideMenuButtonPopup({
  anchorRect, onClose, keepOpenSelector, title, footer, children,
}) {
  const panelRef = useRef(null);
  const [top, setTop] = useState(anchorRect.top);

  useEffect(
    () => {
      const onDocMouseDown = (event) => {
        if (panelRef.current?.contains(event.target)) return;
        if (keepOpenSelector && event.target.closest?.(keepOpenSelector)) return;
        onClose();
      };
      const onKeyDown = (event) => {
        if (event.key === 'Escape') onClose();
      };
      document.addEventListener('mousedown', onDocMouseDown);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('mousedown', onDocMouseDown);
        document.removeEventListener('keydown', onKeyDown);
      };
    },
    [onClose, keepOpenSelector],
  );

  // Re-measured on every content growth, so the box rides up only once it must.
  useLayoutEffect(
    () => {
      const element = panelRef.current;
      if (!element) return undefined;
      const place = () => setTop(Math.max(
        viewportMargin,
        Math.min(anchorRect.top, window.innerHeight - viewportMargin - element.offsetHeight),
      ));
      place();
      const observer = new ResizeObserver(place);
      observer.observe(element);
      window.addEventListener('resize', place);
      return () => {
        observer.disconnect();
        window.removeEventListener('resize', place);
      };
    },
    [anchorRect.top],
  );

  const maxHeight = Math.min(
    window.innerHeight - (2 * viewportMargin),
    Math.round((window.innerHeight - anchorRect.top - viewportMargin) * heightGrowth),
  );

  const style = {
    top,
    // Overlap the side menu by a few pixels so the popup reads as attached.
    left: anchorRect.right - 4,
    maxHeight,
  };

  return createPortal(
    <div
      ref={panelRef}
      style={style}
      className="fixed z-[1003000] w-max min-w-72 max-w-md flex flex-col overflow-hidden bg-white text-pxTxt rounded-xl shadow-card border border-pxMainOutline p-3"
    >
      {title
        ? <div className="shrink-0 font-semibold text-lg px-1 pb-2">{title}</div>
        : null}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {children}
      </div>

      {footer
        ? <div className="shrink-0">{footer}</div>
        : null}
    </div>,
    document.body,
  );
}

SideMenuButtonPopup.propTypes = {
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  keepOpenSelector: PropTypes.string,
  title: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};

export default SideMenuButtonPopup;
